import { useAuthorityStore } from '@/stores/authority';
import NProgress from '@/configs/nprogress';
// import { checkDataAuth as checkDataAuthInvoke } from '@/apis/modules/auth/profile/profile';
import {
  getGlobalParamForLogged,
  getGlobalParamForUnLogged,
} from '@/apis/modules/base/home';
import { logout, getLoginInfo, checkDataAuth } from '@/apis/modules/auth/auth';

import { useBaseInfoStore } from '@/stores/base-info';
import useAxiosAbortStore from '@/stores/base/axios-abort';
import { integrate } from '@/utils/common/function-util';

import {
  getClientTimeByDiff,
  updateTimeDiff,
} from '@/utils/common/server-client-time';
import cookies from 'js-cookie';
import {
  redirectLogOut,
  redirectMobileLogOut,
  isRedirectWxMessageTransfer,
  redirectWxMessageTransfer,
} from '@/utils/common/redirect-util';

const useBaseInfo = useBaseInfoStore();
const useAuthority = useAuthorityStore();

/**
 * 检查store中是否存在登录信息和身份标识
 */
const checkStoreIsExistAuthInfo = () => {
  if (useAuthority.authToken.name) {
    return true;
  }
  return false;
};

/**
 * 检查cookie中是否存在身份标识
 */
const checkCookieIsExistAuthInfo = () => {
  // cookie中更新token信息
  if (cookies.get('UToken')) {
    useAuthority.setAuthToken(JSON.parse(cookies.get('UToken')));
    return true;
  }
  return false;
};

/**
 * 刷新登录信息
 *
 * @returns 返回刷新后的登录信息
 */
const authGetLoginInfo = async (data) => {
  const res = await getLoginInfo(data).catch((error) => {
    console.error(error);
    throw error;
  });
  return res;
};

/**
 * 保存权限列表
 * @param {*} data
 */
const saveFunctionList = (data) => {
  const { menuList } = data;
  const dev =
    import.meta.env.MODE === 'development' ||
    import.meta.env.MODE === 'resident';

  // 开发环境添加示例功能
  if (dev) {
    menuList.push({
      iconClass: 'Menu',
      id: 9901,
      isShowInNav: 1,
      levels: 1,
      menuCode: 'M9901',
      menuName: '示例功能',
      parentId: null,
      sortNum: 99,
      menuUrl: '',
    });
    menuList.push({
      iconClass: 'Menu',
      id: 9921,
      isShowInNav: 1,
      levels: 2,
      menuCode: 'M9921',
      menuName: '基础列表',
      parentId: 9901,
      sortNum: 1,
      menuUrl: '/admin/demo/list-basic',
    });
    menuList.push({
      iconClass: 'Menu',
      id: 9922,
      isShowInNav: 1,
      levels: 2,
      menuCode: 'M9922',
      menuName: '固定表头列表',
      parentId: 9901,
      sortNum: 2,
      menuUrl: '/admin/demo/list-fix-header',
    });
    menuList.push({
      iconClass: 'Menu',
      id: 9924,
      isShowInNav: 1,
      levels: 2,
      menuCode: 'M9924',
      menuName: '自定义Icon列表',
      parentId: 9901,
      sortNum: 4,
      menuUrl: '/admin/demo/custom-svg-icons',
    });
    menuList.push({
      iconClass: 'Menu',
      id: 9925,
      isShowInNav: 1,
      levels: 2,
      menuCode: 'M9925',
      menuName: '按钮列表',
      parentId: 9901,
      sortNum: 5,
      menuUrl: '/admin/demo/button-list',
    });
    menuList.push({
      iconClass: 'Menu',
      id: 9926,
      isShowInNav: 1,
      levels: 2,
      menuCode: 'M9926',
      menuName: 'Feedback反馈',
      parentId: 9901,
      sortNum: 6,
      menuUrl: '/admin/demo/feedback-demo',
    });
  }

  menuList.sort((a, b) => a.sortNum - b.sortNum);

  // 个人中心几个菜单判断
  const personalFunctionList = menuList.filter(
    (f) => useAuthority.personalMenuCodes.indexOf(f.menuCode) !== -1,
  );

  useAuthority.setLoginInfoAndFunction({
    authorityCodeList: data.authorityCodeList,
    loginInfo: data.user,
    originalFunctionList: menuList,
    functionList: integrate(
      menuList.filter(
        (f) =>
          !personalFunctionList.some((fx) => fx.menuCode === f.menuCode) &&
          f.isShowInNav,
      ),
    ),
    personalFunctionList,
  });
};

/**
 * 将登录信息保存到本地存储
 *
 * @param res 响应数据
 */
const saveLoginInfoToStore = (res) => {
  useAuthority.setLoginInfo(res.data.user);
  res.data.token.clientTimestamp = getClientTimeByDiff(
    new Date(res.data.token.timestamp),
  ).getTime();
  useAuthority.setAuthToken(res.data.token);
  saveFunctionList(res.data);
};

/**
 * 保存身份标识等信息到cookie
 * @param {*} res 返回信息
 */
const saveAuthInfoToCookie = (res) => {
  // 存入身份标识
  cookies.set('UToken', JSON.stringify(res.data.token), {
    expires: res.data.token.timeout,
  });
  // 存入用户类型
  cookies.set('userType', res.data.user.userType, {
    expires: res.data.token.timeout,
  });
};

/**
 * 退出登录
 * @returns
 */
const authLogout = async () => {
  const res = await logout({}).catch((error) => {
    // console.log(error);
    console.error('退出登录失败');
    throw error;
  });
  return res;
};

/**
 * 退出登录（用于移动端）
 * @returns
 */
const logoutForMobile = async () => {
  const res = await logout({}).catch((error) => {
    console.error('退出登录失败');
    throw error;
  });
  // 清除store和cookie
  useAuthority.setLoginInfo({});
  useAuthority.setAuthToken({});
  saveFunctionList({ menuList: [] });
  cookies.remove('UToken');
  cookies.remove('userType');

  redirectMobileLogOut();

  return res;
};

/**
 *检查数据权限
 * @param {*} dataAuth
 * @returns  true=有数据权限，false=无数据权限
 */
const checkDataAuthInvoke = async (dataAuth) => {
  const res = await checkDataAuth(dataAuth).catch((error) => {
    console.log(error);
  });
  if (res.code === '00000') {
    return res.data;
  }
  return false;
};

/**
 * 判断当前页面是否需要登录布局
 *
 * @returns 返回需要登录布局的页面类型数组，包括 'admin'、'mobile'、'front'
 */
const needLoginLayout = () => {
  return ['admin', 'mobile', 'front'];
};

/**
 * 处理默认页面
 *
 * @returns 返回默认页面的 URL
 */
const handleDefaultPagePage = () => {
  const { defaultPage } = useAuthority;

  if (defaultPage.defaultPageUrl) {
    //
    return defaultPage.defaultPageUrl;
  }
  return '/';
};

/**
 * 对登录页附带的url处理
 * 1. 登录成功后，如果携带了returnUrl，则跳转到returnUrl
 * 2. 如果登录者有此url的权限，则进入
 * 3. 如果登录者没有此url的权限，则进入首页
 * 4. 如果登录者没有此url的权限，且登录者有其他权限，则进入第一个权限
 * @param fromParam from路由对象
 */
const returnUrlProcessLogic = (fromParam) => {
  const { query } = fromParam;
  const { returnUrl } = query;
  if (
    returnUrl &&
    !useAuthority.originalFunctionList.some((f) => f.menuUrl === returnUrl)
  ) {
    return query.returnUrl;
    // return handleDefaultPagePage();
  }
  return '';
};

/**
 * 更新服务器时间与客户端时间的时间差
 */
const globalParamForUnLogged = async () => {
  const res = await getGlobalParamForUnLogged().catch((error) =>
    console.error(error),
  );

  // 更新服务端与客户端的时间差
  updateTimeDiff();
  return res;
};

/**
 * 全局参数(已登录)
 */
const globalParamForLogged = async () => {
  // 获取全局参数
  const res = await getGlobalParamForLogged().catch((error) =>
    console.error(error),
  );
  // 存储全局参数
  useBaseInfo.setServerGlobalParams(res.data);
  // 更新服务端与客户端的时间差
  updateTimeDiff();
};

/**
 * 处理页面重定向逻辑
 *
 * @param to 目标路由
 * @param from 当前路由
 * @param next 重定向回调函数
 * @returns void
 */
const handleRedirectToNext = async (to, from, next) => {
  // 获取跳转地址，为空跳转到路径带有的跳转地址，不为空跳转返回的指定地址
  const returnUrl = returnUrlProcessLogic(from);
  // const hasDataAuth = await checkToRouterDataAuth(to);
  // if (!hasDataAuth) {
  //   const fromPathForLogin = from.path.toLowerCase().indexOf('/login') === -1;
  //   next(from.name && fromPathForLogin ? false : '/');
  // } else

  if (returnUrl) {
    next(returnUrl);
    delete from.query.returnUrl;
  } else {
    next();
  }
};

/**
 * 处理数据权限校验的异步函数
 *
 * @param {Object} to - 目标路由对象
 * @param {Object} from - 来源路由对象
 * @param {Function} next - Vue Router的next函数
 * @returns {Promise<boolean>} - 返回Promise对象，表示权限校验结果
 */
const handleDataAuthCheck = async (to, from, next) => {
  const { dataAuth } = to.meta;
  if (dataAuth) {
    useAuthority.setMenuUrl(to.path);
    const result = await checkDataAuthInvoke(dataAuth);
    if (!result) {
      // 跳转到通用无数据权限界面
      ElMessageBox.alert(
        `
      <p>无法访问功能 <span style="color:var(--el-color-warning);">${to.meta.targetName}</span></p>
      <p>原因：您没有 <span style="color:var(--el-color-warning);">${to.meta.targetName}</span> 的数据权限</p>
      `,
        '提示',
        {
          dangerouslyUseHTMLString: true,
          type: 'warning',
          showClose: false,
          // modal: false,

          customClass: 'auth-message-box',
          closeOnClickModal: true,
          'close-on-press-escape': true,
          confirmButtonText: '关闭',
        },
      ).then(() => {});

      const isNotFromLogin = from.path.toLowerCase().indexOf('/login') === -1;
      next(from.name && isNotFromLogin ? false : '/');
      return false;
    }
    return true;
  }
  return true;
};

/**
 * 路由守卫函数，用于在路由跳转前执行一系列逻辑判断
 *
 * @param to 目标路由对象
 * @param from 起始路由对象
 * @param next 路由跳转回调函数
 * @returns 无返回值，通过调用 next 函数实现路由跳转
 */
const beforeEachUtil = async (to, from, next) => {
  // 检查是否因在其他设备登录被踢出（WebSocket通知后刷新页面的情况）
  const kickoutMsg = localStorage.getItem('kickout_msg');
  if (kickoutMsg) {
    localStorage.removeItem('kickout_msg');
    NProgress.done();
    if (to?.meta.layout === 'mobile') {
      const { showDialog: vantShowDialog } = await import('vant');
      vantShowDialog({
        title: '提示',
        message: kickoutMsg,
        confirmButtonText: '去登录',
        showCancelButton: false,
        closeOnClickOverlay: false,
      }).then(() => {
        redirectMobileLogOut();
      });
    } else {
      ElMessageBox.alert(kickoutMsg, {
        type: 'warning',
        showClose: false,
        confirmButtonText: '去登录',
      }).then(() => {
        redirectLogOut();
      });
    }
    next(false);
    return;
  }

  if (to.meta.needSetToken) {
    cookies.remove('UToken');
    const token = JSON.parse(to.query.token);
    useAuthority.setAuthToken(token);
    cookies.set('UToken', to.query);
  }
  if (!to.meta.notNeedProgress) {
    NProgress.start();
  }
  useAxiosAbortStore().cancelAll(); // 取消所有请求
  let globalRes = {};
  if (!useBaseInfo.latestServerTime) {
    globalRes = await globalParamForUnLogged();
    useBaseInfo.setServerGlobalParamsUnLogged(globalRes.data);
  }
  // 存储全局参数
  // 去登录页或不需要验证的layout，放行
  if (to.meta.isLogin || !needLoginLayout().some((f) => f === to.meta.layout)) {
    // 初始化全局参数
    // await initGlobalParam();
    // 携带token
    if (to.meta.takeToken) {
      checkCookieIsExistAuthInfo();
    }
    next();
  } else if (checkStoreIsExistAuthInfo()) {
    // 检查store是否存在登录信息-检查登录反馈的token，登录成功token也要存cookie
    // await globalParamForLogged();
    // 处理跳转逻辑
    if (!useBaseInfo.globalDataDic) {
      await globalParamForLogged();
    }

    if (!(await handleDataAuthCheck(to, from, next))) {
      return false;
    }

    await handleRedirectToNext(to, from, next);
  } else if (checkCookieIsExistAuthInfo()) {
    // 检查cookie中是否存在身份标识
    // 重新获取登录反馈的token，并存入store中
    const res = await authGetLoginInfo({
      terminal: useAuthority.terminal,
    }).catch((error) => {
      console.error(error);
      if (to?.meta.layout === 'mobile') {
        redirectMobileLogOut();
      } else if (isRedirectWxMessageTransfer(to)) {
        redirectWxMessageTransfer(to);
      } else {
        redirectLogOut();
      }
    });

    const uToken = cookies.get('UToken');
    const token = 'token';
    res.data[token] = JSON.parse(uToken);
    saveLoginInfoToStore(res);

    await globalParamForLogged();

    if (!(await handleDataAuthCheck(to, from, next))) {
      return false;
    }

    // 处理跳转逻辑
    await handleRedirectToNext(to, from, next);
  } else if (!to.meta.notCheckAuth) {
    // 无任何登录信息，又不是不需要检查是否登录的，直接跳转到登录界面
    // 否则 跳转到登录页面
    if (to?.meta.layout === 'mobile') {
      redirectMobileLogOut();
    } else if (isRedirectWxMessageTransfer(to)) {
      redirectWxMessageTransfer(to);
    } else {
      redirectLogOut();
    }
  } else {
    // 最后默认什么都不处理直接跳转
    next();
  }
};

/**
 * 退出登录
 */
const logOutRequest = async () => {
  await authLogout().catch((error) => {
    console.log(error);
    console.error('退出登录失败');
  });
  redirectLogOut();
};

/**
 * 保存路由信息
 *
 * @param routers 路由信息数组
 * @returns 无返回值
 */
const saveRouters = (routers) => {
  useAuthority.setRouters(routers);
};

export {
  authLogout,
  logoutForMobile,
  beforeEachUtil,
  logOutRequest,
  saveAuthInfoToCookie,
  saveLoginInfoToStore,
  saveRouters,
};
